<template>
  <div class="tech-page">
    <!-- 核心技术体系 -->
    <section class="tech-section container">
      <header class="tech-block-head" data-aos="fade-up">
        <div class="tech-head-text">
          <h2 class="tech-block-title">
            核心技术体系
          </h2>
          <p class="tech-block-sub">完全自主可控</p>
          <p class="tech-block-desc">
            海曦技术以全栈国产化、软硬协同、安全可信为主线，构建“底层算力→算法平台→场景应用”的完整技术闭环，实现核心技术全链路自主可控。
          </p>
        </div>
      </header>

      <div class="tech-card-row" data-aos="fade-up" data-aos-delay="60">
        <article v-for="card in coreCards" :key="card.title" class="tech-card tech-card--core">
          <LazyBg :src="card.bg" />
          <h3 class="tech-card-title">{{ card.title }}</h3>
          <div class="tech-card-body">
            <p v-for="(para, i) in card.paragraphs" :key="i">{{ para }}</p>
          </div>
        </article>
      </div>
    </section>

    <!-- 产业生态合作 -->
    <section class="tech-section container tech-section--divider">
      <header class="tech-block-head" data-aos="fade-up">

        <div class="tech-head-text">
          <h2 class="tech-block-title"> 产业生态合作</h2>
          <p class="tech-block-sub">开放协同，共建共赢</p>
          <p class="tech-block-desc">
            海曦技术以开放协同为理念，联合产业链上下游打造国产化AI生态共同体，加速技术落地与行业普惠。
          </p>
        </div>
      </header>

      <div class="tech-card-row" data-aos="fade-up" data-aos-delay="60">
        <article v-for="card in ecoCards" :key="card.title" class="tech-card tech-card--eco">
          <LazyBg :src="card.bg" />
          <h3 class="tech-card-title">{{ card.title }}</h3>
          <div class="tech-card-body">
            <p v-for="(para, i) in card.paragraphs" :key="i">{{ para }}</p>
          </div>
        </article>
      </div>
    </section>

    <!-- 合作伙伴 -->
    <section class="tech-partners">
      <div class="container">
        <p class="tech-partners-intro" data-aos="fade-up">
          海曦深耕科技领域，携手 20 + 优质合作伙伴。他们遍布各地，资源共享、优势互补，合力为您打通每一条科技脉络。
        </p>
        <img src="../assets/ppf.png" alt="合作伙伴" class="tech-partners-img" loading="lazy" decoding="async">

      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import LazyBg from '@/components/LazyBg.vue';
import coreBg1 from '@/assets/jsyst1_1.png';
import coreBg2 from '@/assets/jsyst1_2.png';
import coreBg3 from '@/assets/jsyst1_3.png';
import ecoBg1 from '@/assets/jsyst2_1.png';
import ecoBg2 from '@/assets/jsyst2_2.png';
import ecoBg3 from '@/assets/jsyst2_3.png';
import { useSeo } from '@/composables/useSeo';

type StackCard = {
  title: string;
  paragraphs: string[];
  bg: string;
};

const coreCards: StackCard[] = [
  {
    bg: coreBg1,
    title: '| 国产化算力适配',
    paragraphs: [
      '芯片深度适配：深度适配主流国产AI芯片，支持多芯片异构纳管与性能调优，高效解决训推兼容、集群调度等核心难题。',
      '端边云协同架构：本地推理可用、断网持续运行、数据不出域，精准适配医疗、校园等高安全合规场景。'
    ]
  },
  {
    bg: coreBg2,
    title: '| 多模态AI算法',
    paragraphs: [
      '自研融合算法：整合视觉、语音、文本、行为特征，打造心理筛查、医疗影像识别等场景的行业级高准确率能力。',
      '模型轻量化技术：通过行业大模型蒸馏压缩，模型体积缩减70%，在国产硬件上实现实时推理与低功耗运行。',
      '隐私计算体系：搭载联邦学习、差分隐私等技术，全程保障敏感数据安全，满足数据合规要求。'
    ]
  },
  {
    bg: coreBg3,
    title: '| 安全可靠体系',
    paragraphs: [
      '全链路安全防线：硬件级加密、可信启动、固件防护，构建从底层到应用层的全栈安全体系。',
      '权威合规认证：通过等保、信创、医疗数据安全、教育数据合规等多重权威认证，全面满足政企行业监管要求。'
    ]
  }
];

const ecoCards: StackCard[] = [
  {
    bg: ecoBg1,
    title: '| 芯片生态合作',
    paragraphs: [
      '与国内主流AI芯片厂商深度战略合作，完成芯片-硬件-算法全栈适配与联合优化，提供稳定、高效的国产化算力整体解决方案。'
    ]
  },
  {
    bg: ecoBg2,
    title: '| 产学研生态',
    paragraphs: [
      '联合高校、科研院所共建AI联合实验室，聚焦医疗AI、青少年心理筛查、智算中心等核心方向开展技术攻关与成果转化。',
      '共建人才培养基地，定向输送智算硬件、AI算法、行业实施等复合型专业人才，支撑产业长期发展。'
    ]
  },
  {
    bg: ecoBg3,
    title: '| 行业生态伙伴',
    paragraphs: [
      '联手ISV/SI、系统集成商及行业服务商，打造标准化解决方案与联合交付体系，提升项目交付效率与质量。',
      '与医疗、教育、政务、金融、工业等领域ISV/SI、系统集成商及行业服务商深度合作，打造标准化解决方案与联合交付体系，提升项目交付效率与质量。'
    ]
  }
];



useSeo({
  title: '技术与生态',
  description: '海曦核心技术体系、产业生态合作与合作伙伴网络。'
});
</script>

<style scoped lang="scss">
.tech-page {
  padding: 100px 0 72px;
  background: linear-gradient(180deg, #f5f9fc 0%, #ffffff 32%, #ffffff 100%);
  color: #1f2937;
}



.tech-section--divider {
  border-top: 1px solid rgba(15, 23, 42, 0.06);
  padding-top: 56px;
}

.tech-block-head {

  margin-bottom: 32px;
}

.tech-head-text {
  min-width: 0;
}

.tech-block-title {
  margin: 0 0 36px;
  font-size: 40px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 16px;
}

.tech-block-sub {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: #45c1cf;
}

.tech-block-desc {
  margin: 0;
  font-size: 18px;
  line-height: 1.8;
  font-weight: 700;
}

.tech-card-row {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: clamp(16px, 2.5vw, 28px);
}

.tech-card {
  position: relative;
  border-radius: 12px;
  padding: 64px;
  min-height: 564px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.12);
  overflow: hidden;
}
.tech-card--core {
  color: #ffffff;
  border: 1px solid rgba(255, 255, 255, 0.08);
}
.tech-card--eco {
  color: #ffffff;
  border: 1px solid rgba(255, 255, 255, 0.12);
}

.tech-card-title {
  position: relative;
  z-index: 1;
  margin: 0 0 42px;
  font-size: 16px;
  font-weight: 500;
  line-height: 1.35;
  letter-spacing: 0.02em;
}

.tech-card-body {
  position: relative;
  z-index: 1;
  flex: 1;

  p {
    margin: 0 0 22px;
    font-size: 14px;
    line-height: 1.75;
    color: #fff;
  }

  p:last-child {
    margin-bottom: 0;
  }
}

.tech-partners-intro {
  color: #333;
  font-size: 18px;
  line-height: 32px;
  margin: 36px 0;
}

.tech-partners-img {
  width: 100%;
}

@media (max-width: 1024px) {
  .tech-card-row {
    grid-template-columns: 1fr;
  }

  .tech-card {
    min-height: 0;
  }

  .tech-logo-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .tech-logo-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .tech-block-head {
    gap: 10px;
  }
}
</style>
